import { observer } from 'mobx-react-lite';
import React from 'react';
import Grid from '@material-ui/core/Grid';
import { confirmAlert } from 'react-confirm-alert';
import data from 'emoji-mart/data/facebook.json';
import NimblePicker from 'emoji-mart/dist/components/picker/nimble-picker';
import NimbleEmojiIndex from 'emoji-mart/dist/utils/emoji-index/nimble-emoji-index';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { useTranslation } from 'react-i18next';
import Hidden from '@material-ui/core/Hidden';
import EditLocationIcon from '@material-ui/icons/EditLocation';
import { makeStyles } from '@material-ui/styles';
import Input from '@material-ui/core/Input';
import styles from './ChatInput.module.css';
import * as utils from '../../../../helpers/utils';
import { useRootStore } from '../../../../mobx/bridge';
import CannedMessagesList from '../CannedMessagesList/CannedMessagesList';
import TransferLocationsList from '../TransferLocationsList/TransferLocationsList';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import DropzoneWrapper from '../DropzoneWrapper/DropzoneWrapper';
import { getCustomerFromSmsChannel } from '../../../../helpers/contactHelpers';

const snackBarPosition = {
  vertical: 'bottom',
  horizontal: 'center',
};

const snackBarClassesOverride = {
  anchorOriginBottomCenter: styles.charLimitSnackbar,
};

const snackBarClassesOverrideWithPreview = {
  anchorOriginBottomCenter: styles.charLimitSnackbarWithPreview,
};

// eslint-disable-next-line
const emojiRegex = /(:-?[0-9A-Za-z()\/|])\s/g;

const bubbleMenu = makeStyles({
  root: {
    boxShadow: '0px 0px 6px #0000001A',
    border: '1px solid #D0D5E1',
    width: '409px',
    padding: '0 0 12px 0',
    overflow: 'visible',
    borderRadius: '7px',
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: '-12px',
      right: '5px',
      zIndex: '1001',
      width: '0',
      height: '0',
      border: '7px solid transparent',
      borderBottom: '0',
      borderTopColor: '#ffffff',
      marginRight: '7px',
      marginBottom: '7px',
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '-14px',
      right: '2px',
      zIndex: '1000',
      width: '0',
      height: '0',
      border: '8px solid transparent',
      borderBottom: '0',
      borderTopColor: '#dfdfdf',
      marginRight: '8px',
      marginBottom: '8px',
    },
  },
});

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const ChatInput = observer(function ChatInput() {
  const {
    commonStore,
    settingsStore,
    messageStore,
    messageTypeStore,
    smsDeliveryStatusStore,
    sendingEvents,
    autoReplyV2Store,
  } = useRootStore();

  const { t } = useTranslation();

  const settings = settingsStore.getLoggedUserSettings;

  const maxLength =
    settings &&
    settings.SelectedChannel &&
    settings.SelectedChannel.ChannelType.TypeId === utils.ChannelTypeEnum.SMS.TypeId
      ? 1600
      : 4000;

  const [emojiMenuVisible, setEmojiMenuVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [transferConversationAnchorEl, setTransferConversationAnchorEl] = React.useState(null);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const emojiContainer = React.useRef(null);
  const emojiButton = React.useRef(null);
  const chatInputRef = React.useRef(null);
  const isUserTyping = React.useRef(false);

  const emojiIndex = new NimbleEmojiIndex(data);

  const popperClass = bubbleMenu();

  const handleCannedMessageClick = (event) => {
    setAnchorEl(event.currentTarget);
    commonStore.setCannedMessagesListOpen(true);
  };

  const handleClose = () => {
    commonStore.setCannedMessagesListOpen(false);
    setAnchorEl(null);
  };

  const handleTransferConversationClick = (event) => {
    setTransferConversationAnchorEl(event.currentTarget);
    commonStore.setTransferLocationListOpen(true);
  };

  const handleTransferConversationClose = () => {
    commonStore.setTransferLocationListOpen(false);
    setTransferConversationAnchorEl(null);
  };

  const handleClick = React.useCallback(
    (e) => {
      if (
        emojiContainer &&
        emojiContainer.current &&
        emojiButton &&
        emojiButton.current &&
        !emojiContainer.current.contains(e.target) &&
        !emojiButton.current.contains(e.target) &&
        emojiMenuVisible
      ) {
        setEmojiMenuVisible(false);
        document.removeEventListener('mousedown', handleClick, false);
      }
    },
    [emojiMenuVisible]
  );

  React.useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  });

  React.useEffect(() => {
    if (emojiMenuVisible) {
      document.addEventListener('mousedown', handleClick, false);
    } else {
      document.removeEventListener('mousedown', handleClick, false);
    }

    return function cleanup() {
      document.removeEventListener('mousedown', handleClick, false);
    };
  }, [emojiMenuVisible, handleClick]);

  React.useEffect(() => {
    if (commonStore.selectedCannedMessage) {
      // setChatInput(commonStore.selectedCannedMessage);
      commonStore.setFullModeChatInput(commonStore.selectedCannedMessage);
      commonStore.setSelectedCannedMessage('');
    }
  }, [commonStore, commonStore.selectedCannedMessage]);

  const closeCharLimitSnackbar = () => {
    setOpenSnackbar(false);
  };

  const isInputTextGreaterThanLimit = commonStore?.fullModeChatInput?.length >= maxLength;
  React.useEffect(() => {
    if (isInputTextGreaterThanLimit) {
      setOpenSnackbar(true);
    }

    return () => {
      closeCharLimitSnackbar();
    };
  }, [isInputTextGreaterThanLimit]);

  const resetData = React.useCallback(() => {
    commonStore.setIsUploading(false);
    commonStore.setDragAndDropChildElementCounter(0);
    commonStore.setFullModeChatFileUpload(null);
  }, [commonStore]);

  const emitContactStartedTypingEvent = () => {
    sendingEvents.socketEmitContactStartedTyping({
      channelId: settings.SelectedChannel._id,
      contactId: commonStore.getLoggedUser._id,
    });
  };

  const emitContactStoppedTypingEvent = () => {
    sendingEvents.socketEmitContactStoppedTyping({
      channelId: settings.SelectedChannel._id,
      contactId: commonStore.getLoggedUser._id,
    });
  };

  const messageSendHandler = (message, fileToSend) => {
    if (settings.SelectedChannel) {
      let msgTypeId;

      if (
        settings.SelectedChannel.ChannelType.TypeId === utils.ChannelTypeEnum.SMS.TypeId &&
        getCustomerFromSmsChannel(settings.SelectedChannel)
      ) {
        msgTypeId = utils.MessageTypeEnum.SMS.TypeId;
      } else if (
        settings.SelectedChannel.ChannelType.TypeId === utils.ChannelTypeEnum.WebChat.TypeId
      ) {
        msgTypeId = utils.MessageTypeEnum.DirectMessage.TypeId;
      }

      if (!msgTypeId) {
        commonStore.setSnackbarMessage(t('ChatInput.FailedToSendMessageError'));
        return;
      }

      if (msgTypeId === utils.MessageTypeEnum.SMS.TypeId) {
        if (!commonStore.locationTrackingNumber) {
          confirmAlert({
            customUI: ({ onClose }) => (
              <CustomConfirmAlert
                title={t('Common.Warning')}
                message={t('Common.NoActiveTrackingNumberForLocationWarning')}
                falseText={t('Common.Close')}
                falseEvent={onClose}
              />
            ),
          });

          return;
        }

        const customerContact = getCustomerFromSmsChannel(settings.SelectedChannel);

        if (customerContact && customerContact.PhoneNumbers.length === 0) {
          confirmAlert({
            customUI: ({ onClose }) => (
              <CustomConfirmAlert
                title={t('Common.Warning')}
                message={t('ChatInput.ContactHasNoPhoneNumbers', {
                  contactName: customerContact.getFullName,
                })}
                falseText={t('Common.Close')}
                falseEvent={onClose}
              />
            ),
          });

          return;
        }
      }

      let receiverPhoneNumber;
      if (
        settings.SelectedChannel.ChannelType.TypeId === utils.ChannelTypeEnum.SMS.TypeId &&
        settings.SelectedChannel.CustomerPhoneNumber
      ) {
        receiverPhoneNumber = settings.SelectedChannel.CustomerPhoneNumber;
      }

      const messageObject = {
        _id: utils.generateGuid(),
        MessageText: message,
        Channel: settings.SelectedChannel,
        Timestamp: new Date().toISOString(),
        Sender: commonStore.getLoggedUser,
        SenderPhoneNumber: commonStore.locationTrackingNumber,
        ReceiverPhoneNumber: receiverPhoneNumber,
        MessageType: messageTypeStore.computedGetByTypeId(msgTypeId),
        SeenBy: [commonStore.getLoggedUser],
        SMSDeliveryStatus:
          msgTypeId === utils.MessageTypeEnum.SMS.TypeId
            ? smsDeliveryStatusStore.computedGetByTypeId(utils.SMSDeliveryStatusEnum.Pending.TypeId)
            : null,
      };

      if (Object.keys(settings.SelectedChannel).length !== 0) {
        sendingEvents.socketEmitMessage(
          messageObject,
          messageObject.Channel._id,
          fileToSend?.acceptedFiles[0],
          fileToSend?.filesData[0]
        );

        emitContactStoppedTypingEvent();

        commonStore.setMaxVisibleMessages(commonStore.maxVisibleMessages + 1);
        commonStore.setOldScrollHeight(0);
        if (!fileToSend) {
          messageStore.UpsertAndGet(messageObject);
        }
      }
    }
  };

  const isSMSChannel =
    settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.SMS.TypeId;
  const isWebChatChannel =
    settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.WebChat.TypeId;

  function handleTurnOffAutoReply() {
    if (isSMSChannel) {
      sendingEvents.socketEmitToggleAutoReplyIsActiveForTextChat(
        autoReplyV2Store.getActiveTextCustomAwayMessageAutoReply
      );
    } else if (isWebChatChannel) {
      sendingEvents.socketEmitToggleAutoReplyIsActiveForWebChat(
        autoReplyV2Store.getActiveWebCustomAwayMessageAutoReply
      );
    }
  }

  function submitHandler(event) {
    event.preventDefault();

    let fileToSend = null;
    let inputCopy = null;

    const acceptedFiles = commonStore.fullModeChatFileUpload?.filter(
      (f) => f.type && f.type.startsWith('image')
    );

    if (acceptedFiles && acceptedFiles.length > 0 && settings.SelectedChannel) {
      commonStore.setDragAndDropChildElementCounter(2);

      const filesData = [];

      acceptedFiles.forEach((file) => {
        const reader = new FileReader();

        filesData.push({
          name: file.name,
          type: file.type,
          sendDate: new Date().toISOString(),
        });

        reader.onabort = () => {
          resetData();
        };

        reader.onerror = () => {
          resetData();
        };

        reader.readAsArrayBuffer(file);
      });

      if (filesData.length !== 0) {
        fileToSend = {
          acceptedFiles,
          filesData,
        };

        commonStore.setFullModeChatFileUpload(null);
        commonStore.setIsUploading(true);
      } else {
        resetData();
      }
    }

    if (commonStore.fullModeChatInput.trim()) {
      // checkMessage(chatInput);
      inputCopy = commonStore.fullModeChatInput;
      if (
        commonStore.fullModeChatInput.length >= 2 &&
        commonStore.fullModeChatInput.length <= 3 &&
        // eslint-disable-next-line no-useless-escape
        /^(:-?[0-9A-Za-z()\/|])$/.test(commonStore.fullModeChatInput) &&
        emojiIndex.search(commonStore.fullModeChatInput).length
      ) {
        // Replaces emojis if they are found in the input
        inputCopy = inputCopy.replace(
          commonStore.fullModeChatInput,
          emojiIndex.search(commonStore.fullModeChatInput)[0].native
        );
      }

      commonStore.setFullModeChatInput('');
      commonStore.setChatInputStyleHeight(0);
    }

    if (inputCopy || fileToSend) {
      messageSendHandler(inputCopy, fileToSend);
    }
  }

  function textChangeHandler(event) {
    let stringWithEmoji = event.target.value;

    // eslint-disable-next-line
    const result = emojiRegex.test(stringWithEmoji);

    if (result) {
      const regexMatch = stringWithEmoji.match(emojiRegex);

      regexMatch.forEach((match) => {
        if (emojiIndex.search(match).length) {
          stringWithEmoji = stringWithEmoji.replace(match, emojiIndex.search(match)[0].native);

          // Prevents cursor from jumping to end of line
          const caret = event.target.selectionStart;
          const element = event.target;
          window.requestAnimationFrame(() => {
            element.selectionStart = caret;
            element.selectionEnd = caret;
          });
        }
      });
    }

    const input = stringWithEmoji || event.target.value;

    if (!!input?.length !== isUserTyping.current) {
      if (input?.length) {
        emitContactStartedTypingEvent();
        isUserTyping.current = true;
      } else {
        emitContactStoppedTypingEvent();
        isUserTyping.current = false;
      }
    }

    // setChatInput(stringWithEmoji || event.target.value);
    commonStore.setFullModeChatInput(stringWithEmoji || event.target.value);
    // Save height of chat input, so it can be used for style changes when there are multiple lines
    commonStore.setChatInputStyleHeight(input?.length > 0 ? event.target?.scrollHeight || 0 : 0);
  }

  function toggleEmojiMenu() {
    setEmojiMenuVisible(!emojiMenuVisible);
  }

  function addEmoji(emoji) {
    commonStore.setFullModeChatInput(commonStore.fullModeChatInput + emoji.native);

    emitContactStartedTypingEvent();
  }

  const clearFileUploads = () => {
    commonStore.setFullModeChatFileUpload(null);
  };

  const transferWebChatToTextConversation = () => {
    const webChatContact = settings?.SelectedChannel?.Contacts?.find(
      (c) => c.ContactType && c.ContactType.TypeId === utils.ContactTypeEnum.WebChatContact.TypeId
    );

    if (webChatContact) {
      sendingEvents.socketEmitTransferWebChatToSMS({
        selectedChannelId: settings.SelectedChannel._id,
        loggedUserId: commonStore.getLoggedUser._id,
        webChatContactId: webChatContact._id,
      });
    }
  };

  const confirmTransferWebChatToTextConversation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('Common.AreYouSure')}
          message={t('ChatInput.ConfirmTransferConversationToTextMessage')}
          trueText={t('Common.Confirm')}
          trueEvent={transferWebChatToTextConversation}
          falseText={t('Common.Close')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const handleEnterKey = (e) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      submitHandler(e);
    }
  };

  let componentToRender = null;

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const transferConversationsOpen = Boolean(transferConversationAnchorEl);
  const transferConversationsId = open ? 'simple-popover' : undefined;

  const contactTyping = settings.SelectedChannel?.UsersTyping?.find(
    (c) => c.contactId !== commonStore.getLoggedUser._id
  );

  const emojiBtnStyles = [styles.btn];
  const cannedMessagesBtnStyles = [styles.btn];
  const sendBtnStyles = [styles.submitBtn];
  let webChatDisabledInputElement;

  if (isSMSChannel) {
    emojiBtnStyles.push(styles.emojiBtnGreen);
    cannedMessagesBtnStyles.push(styles.cannedMessagesBtnGreen);
    sendBtnStyles.push(styles.submitBtnGreen);
  } else if (isWebChatChannel) {
    emojiBtnStyles.push(styles.emojiBtnBlue);
    cannedMessagesBtnStyles.push(styles.cannedMessagesBtnBlue);
    sendBtnStyles.push(styles.submitBtnBlue);

    if (
      settings.SelectedChannel?.ChannelStateType?.TypeId ===
      utils.ChannelStateTypeEnum.Closed.TypeId
    ) {
      webChatDisabledInputElement = (
        <div className={styles.specialContainer}>
          <span>{t('ChatInput.DisabledInput')}</span>
        </div>
      );
    }
  }

  if (settings?.SelectedChannel?.ChannelType) {
    if (isSMSChannel && settings.SelectedChannel?.CustomerPhoneNumber?.IsBlocked) {
      componentToRender = (
        <div className={styles.specialContainer} data-testid="phoneNumberBlocked">
          <span>{t('ChatInput.PhoneNumberIsBlocked')}</span>
        </div>
      );
    } else if (
      (autoReplyV2Store.getActiveTextCustomAwayMessageAutoReply &&
        settings.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.SMS.TypeId) ||
      (autoReplyV2Store.getActiveWebCustomAwayMessageAutoReply &&
        settings.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.WebChat.TypeId)
    ) {
      componentToRender = (
        <div className={styles.specialContainer} data-testid="autoreplyEnabled">
          <span>{t('ChatInput.AutoReplyIsEnabled')}</span>

          <button type="button" onClick={handleTurnOffAutoReply} className={styles.inputSpecialBtn}>
            {t('ChatInput.TurnOff')}
          </button>
        </div>
      );
    } else {
      const controlsShouldBeDisabled =
        !settings.SelectedChannel ||
        settings.SelectedChannel?.ChannelStateType?.TypeId ===
          utils.ChannelStateTypeEnum.Closed.TypeId;

      // eslint-disable-next-line no-nested-ternary
      componentToRender = !controlsShouldBeDisabled ? (
        <React.Fragment>
          <Snackbar
            anchorOrigin={snackBarPosition}
            open={openSnackbar}
            autoHideDuration={5000}
            onClose={closeCharLimitSnackbar}
            classes={
              !commonStore.fullModeChatFileUpload
                ? snackBarClassesOverride
                : snackBarClassesOverrideWithPreview
            }
          >
            <SnackbarContent
              className={styles.charLimitSnackbarContent}
              message={
                <div>
                  <div className={styles.charLimitTitle}>Warning</div>

                  <div className={styles.charLimitItemsContainer}>
                    <svg
                      className={styles.charLimitAlertIcon}
                      focusable="false"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
                    </svg>

                    <div className={styles.charLimitMessage}>
                      {t('ChatInput.MessageTextLimitReached', { maxLength })}
                    </div>
                  </div>
                </div>
              }
            />
          </Snackbar>
          <div className={styles.contactTypingIndicator}>
            {contactTyping
              ? t('ChatInput.ContactIsTyping', { contactName: contactTyping.contactName })
              : null}
          </div>
          <form className={styles.chatInput} onSubmit={submitHandler} data-testid="chatInputForm">
            <div
              className={
                !isWebChatChannel
                  ? styles.inputContainer
                  : [styles.inputContainer, styles.inputContainerNoWrapWebChatSmallerScreens].join(
                      ' '
                    )
              }
            >
              <Input
                multiline
                disableUnderline
                onKeyDown={handleEnterKey}
                rowsMax={8}
                onChange={textChangeHandler}
                value={commonStore.fullModeChatInput}
                placeholder={t('ChatInput.TypeMessagePlaceholder')}
                inputRef={chatInputRef}
                inputProps={{
                  maxLength,
                  'data-testid': 'chatInput',
                }}
                classes={{
                  root: styles.chatInputRoot,
                  input: styles.chatInputTextArea,
                }}
              />

              <div>
                <Tooltip
                  PopperProps={{ disablePortal: true }}
                  arrow
                  enterDelay={1000}
                  title={t('Common.Send')}
                  data-testid="chatInputSendBtn"
                >
                  <button type="submit" className={sendBtnStyles.join(' ')} />
                </Tooltip>
              </div>

              {commonStore.fullModeChatFileUpload ? (
                <React.Fragment>
                  <div className={styles.break} />

                  <div className={styles.imagePreviewContainer} data-testid="imagePreviewContainer">
                    <img
                      className={styles.imagePreview}
                      src={
                        commonStore.fullModeChatFileUpload[0]
                          ? URL.createObjectURL(commonStore.fullModeChatFileUpload[0])
                          : null
                      }
                      alt={t('ChatInput.Preview')}
                    />

                    <button
                      type="button"
                      onClick={clearFileUploads}
                      className={styles.clearFileUploads}
                    />
                  </div>
                </React.Fragment>
              ) : null}
            </div>

            <Hidden smDown>
              <Tooltip
                PopperProps={{ disablePortal: true }}
                arrow
                enterDelay={1000}
                title={t('ChatInput.Emoji')}
              >
                <button
                  ref={emojiButton}
                  type="button"
                  onClick={toggleEmojiMenu}
                  className={emojiBtnStyles.join(' ')}
                />
              </Tooltip>
            </Hidden>

            {!isWebChatChannel ? (
              <DropzoneWrapper
                channel={settings.SelectedChannel}
                displayUploadButton
                isSMSChannel={isSMSChannel}
              />
            ) : null}

            {isWebChatChannel && commonStore?.getTransferLocationsList?.length ? (
              <Tooltip
                // PopperProps={{ disablePortal: true }}
                arrow
                enterDelay={1000}
                title={t('ChatInput.TransferConversationToAnotherShop')
                  .split('\n')
                  .map((str, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                      {str} <br />
                    </React.Fragment>
                  ))}
              >
                <button
                  type="button"
                  className={styles.btn}
                  onClick={handleTransferConversationClick}
                >
                  <EditLocationIcon style={{ color: '#1592e6' }} />
                </button>
              </Tooltip>
            ) : null}

            {isWebChatChannel && commonStore?.getHasAccessToTextChats ? (
              <Tooltip
                // PopperProps={{ disablePortal: true }}
                arrow
                enterDelay={1000}
                title={t('ChatInput.TransferToTextMessage')
                  .split('\n')
                  // eslint-disable-next-line sonarjs/no-identical-functions
                  .map((str, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                      {str} <br />
                    </React.Fragment>
                  ))}
              >
                <button
                  type="button"
                  className={[styles.btn, styles.transferWebChaToSMSBtn].join(' ')}
                  onClick={confirmTransferWebChatToTextConversation}
                />
              </Tooltip>
            ) : null}

            {commonStore.transferLocationListOpen ? (
              <Popover
                id={transferConversationsId}
                open={transferConversationsOpen}
                anchorEl={transferConversationAnchorEl}
                onClose={handleTransferConversationClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                classes={{ paper: popperClass.root }}
              >
                <TransferLocationsList />
              </Popover>
            ) : null}

            <Tooltip
              PopperProps={{ disablePortal: true }}
              arrow
              enterDelay={1000}
              title={t('Common.CannedMessages')}
              placement="left"
            >
              <button
                type="button"
                className={cannedMessagesBtnStyles.join(' ')}
                onClick={handleCannedMessageClick}
                data-testid="cannedMessagesChatInputBtn"
              />
            </Tooltip>

            {commonStore.cannedMessagesListOpen ? (
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                classes={{ paper: popperClass.root }}
              >
                <CannedMessagesList />
              </Popover>
            ) : null}
          </form>
          {emojiMenuVisible ? (
            <div ref={emojiContainer} className={styles.emojiMenu}>
              <NimblePicker
                autoFocus
                data={data}
                set="facebook"
                showPreview={false}
                showSkinTones={false}
                onSelect={addEmoji}
                emojiSize={26}
                native
              />
            </div>
          ) : null}
        </React.Fragment>
      ) : (
        webChatDisabledInputElement
      );
    }
  }

  return (
    <Grid component="div" container>
      <Grid component="div" item xs={12}>
        {componentToRender}
      </Grid>
    </Grid>
  );
});

ChatInput.whyDidYouRender = true;
ChatInput.displayName = 'ChatInput';

export default ChatInput;
